import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Send, Trash2, Sparkles, FileText } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, API_BASE } from "@/lib/api";

const SUGGESTIONS = ["Which documents need reviewing this month?", "Suggest controls for working in an occupied office", "Explain the difference between a RAMS and a method statement", "Find our RAMS for the warehouse lighting job"];

export const AskSaflr = ({ open, onOpenChange }) => {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  useEffect(() => {
    if (open) api.get("/assistant/history").then((r) => setMsgs(r.data.reverse())).catch(() => {});
  }, [open]);
  useEffect(() => {
    end.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs]);
  const send = async (m) => {
    const message = (m || text).trim();
    if (!message || busy) return;
    setText("");
    setBusy(true);
    setMsgs((x) => [...x, { role: "user", content: message }, { role: "assistant", content: "", sources: [] }]);
    try {
      const res = await fetch(`${API_BASE}/assistant/chat`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ message }) });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || "Ask Saflr is unavailable");
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const ev = JSON.parse(p.slice(6));
          setMsgs((x) => {
            const c = [...x];
            const last = { ...c[c.length - 1] };
            if (ev.t) last.content += ev.t;
            if (ev.done) last.sources = ev.sources;
            c[c.length - 1] = last;
            return c;
          });
        }
      }
    } catch (e) {
      setMsgs((x) => [...x.slice(0, -1), { role: "assistant", content: e.message }]);
    }
    setBusy(false);
  };
  const cited = (m) => (m.sources || []).filter((s) => m.content.includes(s.doc_number));
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col p-0 sm:max-w-md" data-testid="ask-saflr-drawer">
        <SheetHeader className="border-b px-5 py-4">
          <SheetTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-health" />Ask Saflr
            <button className="ml-auto mr-6 text-muted-foreground hover:text-foreground" aria-label="Clear" data-testid="ask-saflr-clear" onClick={() => api.delete("/assistant/history").then(() => setMsgs([]))}><Trash2 className="h-4 w-4" /></button>
          </SheetTitle>
        </SheetHeader>
        <div className="flex-1 space-y-4 overflow-auto p-5">
          {msgs.length === 0 && (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Your HSE assistant. It can only see your organisation's documents.</p>
              {SUGGESTIONS.map((s) => <button key={s} onClick={() => send(s)} className="block w-full rounded-xl border p-3 text-left text-sm hover:bg-accent">{s}</button>)}
            </div>
          )}
          {msgs.map((m, i) => (
            <div key={i} className={m.role === "user" ? "ml-8 rounded-2xl rounded-br-sm bg-navy p-3 text-sm text-white" : "mr-4 text-sm"} data-testid={`ask-msg-${m.role}`}>
              <p className="whitespace-pre-wrap leading-relaxed">{m.content || (busy && i === msgs.length - 1 ? "Thinking…" : "")}</p>
              {cited(m).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">{cited(m).map((s) => <Link key={s.id} to={`/app/documents/${s.id}`} onClick={() => onOpenChange(false)} className="inline-flex items-center gap-1 rounded-md border px-2 py-0.5 font-mono text-[11px] hover:bg-accent"><FileText className="h-3 w-3" />{s.doc_number}</Link>)}</div>
              )}
            </div>
          ))}
          <div ref={end} />
        </div>
        <div className="border-t p-4">
          <div className="flex gap-2">
            <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder="Ask about your HSE documents…" data-testid="ask-saflr-input" className="resize-none" />
            <Button onClick={() => send()} disabled={busy} size="icon" className="h-auto" data-testid="ask-saflr-send"><Send className="h-4 w-4" /></Button>
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">AI guidance — verify with a competent person.</p>
        </div>
      </SheetContent>
    </Sheet>
  );
};
